import { Button } from '@/components/ui/button.tsx'
import { STORY_STATUSES } from '@/constants/STORY_STATUSES.ts'
import { useProjectId } from '@/hooks/useProjectId.ts'
import { cn } from '@/lib/utils.ts'
import { toReadableDate } from '@/utils/date.ts'
import { useNavigate } from 'react-router-dom'

type Props = {
  releases: ReleaseFlag[]
  showAll?: boolean
  setShowAll?: (b: boolean) => void
}

const rowStyle = 'grid grid-cols-[1fr_78px_78px_78px_78px]'

export const ReleaseTable = ({ releases, showAll, setShowAll }: Props) => {
  const projectId = useProjectId()
  const navigate = useNavigate()

  const getDeadlineOrEstimatedDate = (r: ReleaseFlag): number => {
    if (r.deadline == null) {
      if (r.currentState === STORY_STATUSES.ACCEPTED) {
        return r.acceptedAt ?? 0
      }
      return Number.parseInt(r.projectedCompletionInterval?.split('/')[1] ?? '0')
    }

    return r.deadline
  }

  const getEstimatedDate = (r: ReleaseFlag): number => {
    if (r.currentState === STORY_STATUSES.ACCEPTED) {
      return r.acceptedAt ?? 0
    }

    return Number.parseInt(r.projectedCompletionInterval?.split('/')[1] ?? '0')
  }

  const isBehindSchedule = (r: ReleaseFlag): boolean => {
    const estimated = getEstimatedDate(r)
    const deadlineOrEstimated = getDeadlineOrEstimatedDate(r)
    return estimated - deadlineOrEstimated > 24 * 3600 * 1000
  }

  const completionPercentage = (r: ReleaseFlag): number => {
    if (r.currentState === STORY_STATUSES.ACCEPTED) {
      return 100
    }

    if (r.pointsTotal === 0) {
      return 0
    }

    return Math.round((r.pointsAccepted / r.pointsTotal) * 100)
  }

  return (
    <>
      {releases.length === 0 ? (
        <div className="h-[120px] bg-surface-backgroundElevated rounded-md p-2 border-[1px] border-dashed border-border-default-secondary flex justify-center items-center">
          <p className="font-EN-KR-body1-bold text-tb-blue-400">No releases available. Let’s schedule the first one!</p>
        </div>
      ) : (
        <div className="rounded-md overflow-hidden">
          <div className={cn('h-8 bg-surface-primary ', rowStyle)}>
            <div className="font-EN-KR-body2-bold text-contents-onDarkPrimary px-4 self-center">
              <div className="flex justify-between items-center">
                <p>Releases</p>
                {showAll !== undefined && !showAll && (
                  // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
                  <div
                    className="flex gap-1 items-center cursor-pointer"
                    onClick={() => {
                      setShowAll?.(true)
                    }}
                  >
                    <span className="font-EN-KR-caption-bold text-contents-onDarkPrimary no-underline">
                      Show completed releases
                    </span>
                    <img src="/images/plus-icon.svg" alt="more" />
                  </div>
                )}
              </div>
            </div>
            <div className="font-EN-KR-caption-regular text-contents-accent place-self-center">Total Point</div>
            <div className="font-EN-KR-caption-regular text-contents-accent place-self-center">Point Remaining</div>
            <div className="font-EN-KR-caption-regular text-contents-accent place-self-center">Scheduled</div>
            <div className="font-EN-KR-caption-regular text-contents-accent place-self-center">Completion</div>
          </div>
          <ul className={cn('gap-[1px] auto-rows-[68px]', rowStyle)}>
            {releases.map((release) => {
              return (
                <li key={release.id} className="contents">
                  <div className="bg-surface-backgroundElevated px-4 flex gap-2 justify-center items-center">
                    <div className="relative h-9 w-9 rounded-xs bg-tb-blue-100 flex justify-center items-center">
                      <img src="/images/analytics-release-flag-icon.svg" alt="release flag" />
                      {release.currentState === STORY_STATUSES.ACCEPTED && (
                        <img
                          src="/images/analytics-release-flag-completed-icon.svg"
                          alt="completed"
                          className="absolute bottom-0.5 right-0.5"
                        />
                      )}
                    </div>
                    <div className="flex flex-col gap-2 w-full">
                      <div className="flex justify-between items-center">
                        <p className="font-EN-KR-body2-regular text-contents-primary">{release.name}</p>
                        <Button
                          variant="text"
                          size="min"
                          onClick={() => {
                            navigate(`/project/${projectId}/releases/${release.id}`)
                          }}
                        >
                          release details
                        </Button>
                      </div>
                      <div className="h-2.5 bg-tb-blue-300 rounded-sm">
                        <div
                          style={{
                            width: `${completionPercentage(release)}%`,
                          }}
                          className={cn(
                            'h-2.5 rounded-sm',
                            release.currentState === STORY_STATUSES.ACCEPTED
                              ? 'bg-surface-iterationMarker-done'
                              : 'bg-contents-accent',
                          )}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="bg-surface-backgroundElevated flex justify-center items-center font-EN-KR-body2-bold text-contents-tertiary">
                    {release.pointsTotal}
                  </div>
                  <div className="bg-surface-backgroundElevated flex justify-center items-center font-EN-KR-body2-regular text-contents-tertiary">
                    {release.pointsTotal - release.pointsAccepted}
                  </div>
                  <div className="bg-surface-backgroundElevated flex justify-center items-center font-EN-KR-caption-regular text-contents-tertiary">
                    {release.deadline ? toReadableDate(release.deadline) : 'N/A'}
                  </div>
                  <div
                    className={cn(
                      'bg-surface-backgroundElevated flex flex-col justify-center items-center font-EN-KR-caption-regular text-contents-tertiary',
                      isBehindSchedule(release) && 'text-contents-alert',
                    )}
                  >
                    {release.currentState !== STORY_STATUSES.ACCEPTED && <p>Estimated</p>}
                    <p>{toReadableDate(getEstimatedDate(release))}</p>
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </>
  )
}
